import type {
  Lifecycle,
  Marketplace,
  SplitGroup,
  SplitItem,
  SplitLabel,
  SplitTraceEntry,
} from "./types";

interface RawSplitLabel extends Partial<Omit<SplitLabel, "items">> {
  items?: SplitItem[];
}

/** A raw split-orders row: BE thin projection or the full MSW view-model. */
export interface RawSplitGroup extends Partial<Omit<SplitGroup, "labels" | "trace">> {
  labels?: RawSplitLabel[];
  trace?: SplitTraceEntry[];
  // BE thin-projection field names
  sale_order_id?: string;
  marketplace?: string;
  lifecycle_status?: string;
}

/**
 * Normalise a split-orders row into the `SplitGroup` view-model.
 *
 * `SplitDetail` dereferences `split.labels` (`.length` / `.map`), each
 * `label.items` (`.map`), and `split.trace` (`.map`). The real backend returns
 * a thinner projection that can omit these arrays; MSW serves the full shape.
 * This mapper defaults every dereferenced array so the page can't crash, and
 * passes a full view-model through unchanged.
 */
export function toSplitGroup(r: RawSplitGroup): SplitGroup {
  const labels: SplitLabel[] = (r.labels ?? []).map((l) => ({
    idx: l.idx ?? 0,
    label: l.label ?? "",
    status: l.status ?? "received",
    tracking: l.tracking ?? null,
    rule: l.rule ?? "",
    items: l.items ?? [],
  }));
  return {
    sale: r.sale ?? r.sale_order_id ?? "",
    mkp: r.mkp ?? (r.marketplace as Marketplace | undefined) ?? "shopee",
    date: r.date ?? "",
    cust: r.cust ?? "",
    status: r.status ?? (r.lifecycle_status as Lifecycle | undefined) ?? "received",
    count: r.count ?? labels.length,
    total: r.total ?? 0,
    labels,
    trace: r.trace ?? [],
  };
}
